import { useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import c from 'classnames';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import {
  Query,
  QueryMetricRealtimeUnansweredConversationsArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { whenDefined } from '@lgg/isomorphic/when-defined';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import {
  Card,
  CardMainNumericValue,
  DashboardCardValue,
  emptyValuePlaceholder,
} from 'src/components/pages/dashboard/components/shared';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useTimeHelpers } from 'src/hooks/use-time-helpers';
import { useUrls } from 'src/hooks/use-urls';

export const GET_METRIC_REALTIME_UNANSWERED_CONVERSATIONS_QUERY = gql`
  query GetMetricAverageAnsweredConversations(
    $institutionId: Int!
    $where: MetricRealtimeUnansweredConversationsWhereInput
  ) {
    metricRealtimeUnansweredConversations(institutionId: $institutionId, where: $where) {
      total
      maxWaitingTimeInSeconds
      averageWaitingTimeInSeconds
    }
  }
`;

const UnansweredConversationIcon = styled(Icon)`
  svg {
    width: 24px;
    height: 24px;

    path {
      fill: transparent;
    }
  }
`;

const ContentContainer = styled(FlexRow)`
  align-items: center;
  height: max-content;
  justify-content: space-between;
`;

const StyledSmallCard = styled(Card)`
  &.good {
    border: 1px solid ${({ theme }) => theme.colors.secondaryMint40};
    background: linear-gradient(
      180deg,
      ${({ theme }) => theme.colors.white} 42.62%,
      ${({ theme }) => theme.colors.secondaryMint30} 148.55%
    );
  }

  &.warning {
    border: 1px solid ${({ theme }) => theme.colors.secondaryGold40};
    background: linear-gradient(
      180deg,
      ${({ theme }) => theme.colors.white} 42.62%,
      ${({ theme }) => theme.colors.secondaryGold30} 148.55%
    );
  }

  &.bad {
    border: 1px solid ${({ theme }) => theme.colors.secondaryCoral40};
    background: linear-gradient(
      180deg,
      ${({ theme }) => theme.colors.white} 42.62%,
      ${({ theme }) => theme.colors.secondaryCoral30} 148.55%
    );
  }
  padding: 20px 15px 12px;
`;

const ContentLeftContainer = styled(FlexRow)`
  align-items: center;
`;

const StyledText = styled.span`
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 11px;
  line-height: 12px;
  letter-spacing: -0.11px;
`;

const StrongText = styled(StyledText)`
  color: ${({ theme }) => theme.colors.smalt};
`;

const StyledCardContent = styled(Card.Content)`
  justify-content: flex-end;
`;

const TWENTY_MINUTES_IN_SECONDS = 1200;
const SIXTY_MINUTES_IN_SECONDS = 3600;

export const UnansweredConversationsCard = () => {
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const { t } = useTranslation(['common', 'dashboard']);
  const { targetUserId } = useContext(DashboardFiltersContext);
  const { getConversationsUrl } = useUrls();
  const { formatAbbreviatedComposedDuration } = useTimeHelpers();

  const realTimeUnansweredConversationsQueryResult = useQuery<
    Pick<Query, 'metricRealtimeUnansweredConversations'>,
    Partial<QueryMetricRealtimeUnansweredConversationsArgs>
  >(GET_METRIC_REALTIME_UNANSWERED_CONVERSATIONS_QUERY, {
    variables: {
      institutionId: currentInstitution.id,
      where: whenDefined(targetUserId, (id) => ({
        dimensionContactAssignee: { id: { _eq: id } },
      })),
    },
    onError: handleGraphQLError,
  });

  const { data, loading } = realTimeUnansweredConversationsQueryResult;

  const isEmpty = !loading && data?.metricRealtimeUnansweredConversations === null;

  const metricRealtimeUnansweredConversations =
    data?.metricRealtimeUnansweredConversations;

  const {
    averageWaitingTimeInSeconds = 0,
    maxWaitingTimeInSeconds = 0,
    total = 0,
  } = metricRealtimeUnansweredConversations ?? {};

  const cardClassName = useMemo(() => {
    return match(maxWaitingTimeInSeconds)
      .when(
        (value) => value < TWENTY_MINUTES_IN_SECONDS,
        () => 'good',
      )
      .when(
        (value) => value >= TWENTY_MINUTES_IN_SECONDS && value < SIXTY_MINUTES_IN_SECONDS,
        () => 'warning',
      )
      .when(
        (value) => value >= SIXTY_MINUTES_IN_SECONDS,
        () => 'bad',
      )
      .run();
  }, [maxWaitingTimeInSeconds]);

  return (
    <StyledSmallCard
      variant="small"
      data-lgg-id="dashboard-card-unanswered-conversations"
      className={!loading ? c({ [cardClassName]: true }) : undefined}
      loading={loading}
    >
      <Card.Header
        as="p"
        title={t('dashboard:cards.unansweredConversations.title')}
        cardInfoI18nKey="dashboard:cards.unansweredConversations.tooltipDescription"
        cardTextTitle={t('dashboard:cards.unansweredConversations.title')}
        variant="small"
      />
      <StyledCardContent>
        <ContentContainer>
          <ContentLeftContainer>
            <DashboardCardValue data-lgg-id="unanswered-conversations-value">
              {!isEmpty ? <CardMainNumericValue value={total} /> : 0}
            </DashboardCardValue>
            <FlexColumn>
              <StyledText data-lgg-id="dashboard-card-unanswered-conversations-max-waiting-time">
                <Trans
                  i18nKey="dashboard:cards.unansweredConversations.maxWaitingTime"
                  values={{
                    value: !isEmpty
                      ? formatAbbreviatedComposedDuration({
                          inputInSeconds: maxWaitingTimeInSeconds,
                        })
                      : emptyValuePlaceholder,
                  }}
                  components={{
                    strong: <StrongText />,
                  }}
                />
              </StyledText>
              <StyledText data-lgg-id="dashboard-card-unanswered-conversations-avg-waiting-time">
                <Trans
                  i18nKey="dashboard:cards.unansweredConversations.avgWaitingTime"
                  values={{
                    value: !isEmpty
                      ? formatAbbreviatedComposedDuration({
                          inputInSeconds: averageWaitingTimeInSeconds,
                        })
                      : emptyValuePlaceholder,
                  }}
                  components={{
                    strong: <StrongText />,
                  }}
                />
              </StyledText>
            </FlexColumn>
          </ContentLeftContainer>
          <UnansweredConversationIcon type="unansweredConversation" />
        </ContentContainer>
      </StyledCardContent>
      <Card.Footer
        linkTo={getConversationsUrl('IS_REPLY_PENDING')}
        linkText={t('common:viewNext')}
        variant="small"
        borderColorOverride="flint20"
      />
    </StyledSmallCard>
  );
};
